import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { deepCopy, makeid, removeUndefined } from 'na-lib';
import { TranslateService } from '@ngx-translate/core';
import { CommentsService } from 'src/app/service/comments.service';
import { FormEditFieldOptionsDialogComponent } from '../form-edit-field-options-dialog/form-edit-field-options-dialog.component';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

export class FormFieldKeyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
    selector: 'app-form-edit-field-dialog',
    templateUrl: './form-edit-field-dialog.component.html',
    styleUrls: ['./form-edit-field-dialog.component.css'],
    standalone: false
})
export class FormEditFieldDialogComponent implements OnInit{
  matcher = new FormFieldKeyErrorStateMatcher();
  fieldForm!:FormGroup
  fieldData:any
  disableFields = false
  fieldTitle:string
  translateFields = []
  validations = []
  enableValidations = false

  widgetId: any
  field: any
  fieldId: any
  sectionId:any
  selectedLanguage:any

  @ViewChild(QuillEditorComponent, { static: true }) editor!: QuillEditorComponent;
  private md: MarkdownIt;
  private turndownService: TurndownService;

  quillModules = {
    toolbar: {
      container: '#toolbar',
    },
  };

  constructor(
    public dialogRef: MatDialogRef<FormEditFieldDialogComponent>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService : TranslateService,
    private commentService: CommentsService
  ){
    dialogRef.disableClose = true;
    this.md = new MarkdownIt({ breaks: true });
    this.turndownService = new TurndownService();
  }

  ngOnInit(): void {
    this.fieldData = deepCopy(this.data.values)
    if(this.data.title)
      this.fieldTitle = this.data.title

    let {type, id, title, key, mandatory, value, options, showsAccuracy, selectLocation, gpsLocation, showsMap, validations, showValueAsLabel} = this.fieldData

    this.widgetId = this.data.widgetId
    this.field = this.data.field
    this.fieldId = this.data?.fieldId
    this.sectionId = this.data?.sectionId
    this.selectedLanguage = this.data?.selectedLanguage

    if (this.data.translate)
      this.disableFields = this.data.translate

    if (validations)
      this.validations = validations

    if (type === "options" && options && options?.length!=0){
      options.map( option => {
        if(typeof option === 'string')
          return { id: makeid(3), value: option, title: option}
        else
          return option
      })
    }

    this.fieldForm = this.fb.group({
      id:[{value: id ? id : undefined, disabled: false}],
      key:[ {value:key ? key : undefined, disabled: this.disableFields}, [Validators.required, Validators.pattern('^(?![_\\.])(?!.*[_\\.]$)(?!.*[\'\";,#%&<>(){}\\[\\]/\\\\|`*@$^=+?!]).*$')]],
      mandatory:[{value: mandatory ? mandatory : false, disabled: this.disableFields}],
      title:[{value: title ? title : undefined, disabled: false}],
      value:[{value: value ? value : undefined, disabled: false}],
      type:[ {value: type ? type : undefined, disabled: false}],
      options:[{ value: options ? options : [], disabled: false}],
      showsAccuracy:[{ value:showsAccuracy ? showsAccuracy :false, disabled: this.disableFields }],
      selectLocation:[{ value:selectLocation ? selectLocation :false, disabled: this.disableFields }],
      gpsLocation:[{ value:gpsLocation ? gpsLocation :false, disabled: this.disableFields }],
      showsMap:[{ value:showsMap ? showsMap :false, disabled: this.disableFields }],
      validations:[{ value: this.validations ? this.validations : [], disabled: false}],
      showValueAsLabel: [{ value: showValueAsLabel ? showValueAsLabel : false, disabled: false}],
      valueToSave: [{value: value ? value : undefined, disabled: false}]
    })

    if (type == 'text' || type == 'longText' || type == 'number' || type == 'decimal' || type == 'date' || type == 'dateTime' || type == 'time')
      this.enableValidations = true

    this.fieldForm.get("title")?.valueChanges.subscribe( (newtitlevalue:any) =>{
      if (!this.disableFields) {
        const cleanValue = newtitlevalue.replaceAll(' ', '')
        this.fieldForm.get("key")?.setValue( cleanValue)
      }
    })

    this.fieldForm.get("key")?.valueChanges.subscribe( newkey =>{
      if (newkey.includes(" ")){
        newkey = newkey.replaceAll(" ", "_")
        this.fieldForm.get("key")?.setValue( newkey)
      }
    })

  }

  addNewOptionToField(){
    let dialogRef = this.dialog.open(FormEditFieldOptionsDialogComponent, {
      data: {
        values: {id: makeid(4), title: '', value:''}
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result[1]){
        let options = this.fieldForm.value.options
        let newoption = deepCopy(result[0])
        options.push(newoption)
        this.fieldForm.patchValue({...options})
      }
    });
  }

  editOptionToField(optionSelected, indexSelected){
    const option = deepCopy(optionSelected)
    // if(this.data.translateMode && !this.data.translateData.originalLanguageData){
        // option.title = this.data.valuesTranslated[this.data.selectedLanguage +'.'+ this.widgetId + '_' + this.sectionId + '_' + this.fieldId +'.options_' + option.id + '.title']

    let dialogRef = this.dialog.open(FormEditFieldOptionsDialogComponent, {
      data: {
        values: option,
        index: indexSelected,
        translate: this.disableFields,
        valuesTranslated: this.data.valuesTranslated,
        optionIndex: indexSelected
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result[1]){

        if (this.data.translateMode && !this.data.translateData?.originalLanguageData){
          const {title} = result[0]
          let options = deepCopy(this.fieldForm.value.options)
          const defaultLanguageFieldOption = this.data.defaultLanguageField?.options[indexSelected]
          options[indexSelected].title = title ? title : defaultLanguageFieldOption?.title
          this.data.valuesTranslated[this.data.selectedLanguage +'.'+ this.widgetId + '_' + this.sectionId + '_' + this.fieldId +'.options_' + optionSelected.id + '.title'] = title
          this.fieldForm.patchValue({options: options})
        } else {
          let options = deepCopy(this.fieldForm.value.options)
          options[indexSelected] = result[0]
          this.fieldForm.patchValue({options: options})
        }
      }
    });
  }

  deleteOption(optionSelected, indexSelected){
    let options = deepCopy(this.fieldForm.value.options)
    options.splice(indexSelected,1)
    this.fieldForm.patchValue({options: options})
  }

  onSave(){
    const validations = this.fieldForm.value.validations?.length > 0 ? this.fieldForm.value.validations : undefined
    if (this.disableFields){
      let data
      if (this.fieldForm.value.type === 'options'){
        const {type, id, title, key, mandatory, options} = this.fieldForm.value
        data = {type, id, title, key, mandatory, options}
      } else {
        const {type, id, title, key, mandatory} = this.fieldForm.value
        data = {type, id, title, key, mandatory, validations}
      }
      this.dialogRef.close([ data, true])
    } else {
      if (this.fieldForm.value.key && this.fieldForm.get('key')?.valid){
        let data
        const { type, value, showValueAsLabel } = this.fieldForm.value
        if (type === 'options'){
          const {type, id, title, key, mandatory, options} = this.fieldForm.value
          data = {type, id, title, key, mandatory, options}
        }
        if (type === 'map'){
          const {type, id, title, key, mandatory, showsAccuracy, selectLocation, gpsLocation, showsMap} = this.fieldForm.value

          if (!gpsLocation && !selectLocation){
            const message = this.translateService.instant("SnackBarConstants.GPS_LOCATION_SELECT_LOCATION_BOTH_FALSE_ERROR")
            this.commentService.addSnackBar.emit(message)
            return
          } else
          data = {type, id, title, key, mandatory, showsAccuracy, selectLocation, gpsLocation, showsMap}
        }

        if (type != 'map' && type != 'options'){
          const {type, id, title, key, mandatory} = this.fieldForm.value
          const markdownContent = this.saveMarkdown();
          data = {
            type, id, title, key, mandatory,
            value: value ? markdownContent : undefined, 
            showValueAsLabel: showValueAsLabel ? showValueAsLabel : undefined,  
            validations
          }
        }
        this.dialogRef.close([ removeUndefined(data), true])
      } else {
        const message = this.translateService.instant("SnackBarConstants.MANDATORY_FIELDS_EMPTY")
        this.commentService.addSnackBar.emit(message)
      }
    }

  }
  returnChangesFieldRules($event){
    if($event && $event.newRules){
      this.fieldForm.patchValue({validations: $event.newRules})
    }
  }

  onCancel(){
    this.dialogRef.close([this.fieldData, false])
  }

  loadContent(markdown: string) {
    if (this.quillEditor) {
      const html = this.md.render(markdown); // markdown to HTML
      this.quillEditor.clipboard.dangerouslyPasteHTML(html); // Insert HTML
    }
  }

  getLanguageClass(): string {
    const currentLang = this.translateService.currentLang() || this.translateService.fallbackLang() || 'en';
    return `lang-${currentLang}`;
  }

  private quillEditor: any;
  onEditorCreated(quill: any) {
    this.quillEditor = quill;
    if (this.fieldForm.value.value) {
      this.markdownToQuill(this.fieldForm.value.value);
    }
    // remove unwanted pasted formatting
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
      // define supported styles
      const retainStyles = ['bold', 'italic', 'link', 'list'];

      delta.ops.forEach((op: any) => {
        if (op.attributes) {
          // delete unsupported styles
          Object.keys(op.attributes).forEach((attr) => {
            if (!retainStyles.includes(attr)) {
              delete op.attributes[attr];
            }
          });
        }
      });

      return delta; // cleaned delta
    });
  }

  private isInsertingContent: boolean = false;

  markdownToQuill(markdown: string) {
    if (this.quillEditor) {
      if (this.isInsertingContent) {
        return; // Prevent re-entrance
      }
      this.isInsertingContent = true;
      // Convert markdown to HTML
      const html = this.md.render(markdown);
      this.quillEditor.clipboard.dangerouslyPasteHTML(html);
  
      this.isInsertingContent = false;
    }
  }

  loadTextValueFromMarkdown(){
    const markdownContent = this.saveMarkdown();
    const cleanedAll = this.checkDynamicValuesUnderscore(markdownContent)
    this.fieldForm.get('valueToSave')?.setValue(cleanedAll);
  }

  checkDynamicValuesUnderscore(data){
    let result = [...data.matchAll(/\${(.+)\}/g)];
    let newdata = data
    if(result){
      const value = `\\\\_`
      newdata = newdata.replaceAll(value, '_')

      const value2 = `\\_`
      newdata = newdata.replaceAll(value2, '_')

      const value1 = `\_`
      newdata = newdata.replaceAll(value1,'_')
      data = newdata
    }
    return data
  }

  saveMarkdown() {
    if (this.quillEditor) {
      const html = this.quillEditor.root.innerHTML; // Get HTML
      //HOTFIX: for Quill generating bulleted lists as ordered lists and turndown not recognizing those correctly
      const htmlFixed = html.replace(/<ol>(?=([^<]|<[^\/]|<\/[^o]|<\/o[^l]|<\/ol[^>])*\s*<li data-list="bullet">)/g, '<ul>');
      const markdownContent = this.turndownService.turndown(htmlFixed); // HTML to Markdown
      return markdownContent;
    }
    return '';
  }

}
